import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { supabase } from "./lib/supabaseClient";
import useEntitlement from "./hooks/useEntitlement";

// Pages
import LandingPage from "./pages/LandingPage.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import { PaidAssessmentEntryGate } from "./components/PaidAssessmentAllowance.jsx";
import StartAudit from "./pages/StartAudit.jsx";
import StartAuditLegacy from "./pages/StartAuditLegacy.jsx";
import StartAuditProfile from "./pages/StartAuditProfile.jsx";
import UniversalRiskAudit from "./pages/UniversalRiskAudit.jsx";
import UniversalRiskAuditResults from "./pages/UniversalRiskAuditResults.jsx";
import AINews from "./pages/AINews.jsx";
import Reports from "./pages/Reports.jsx";
import RemediationHub from "./pages/RemediationHub.jsx";
import Pricing from "./pages/Pricing.jsx";
import Subscription from "./pages/Subscription.jsx";
import Terms from "./pages/Terms.jsx";
import Legal from "./pages/Legal.jsx";
import Privacy from "./pages/Privacy.jsx";
import UserGuide from "./pages/UserGuide.jsx";
import Auth from "./pages/Auth.jsx";
import AuthCallback from "./pages/AuthCallback.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import PaymentSuccess from "./pages/PaymentSuccess.jsx";
import History from "./pages/History.jsx";
import Profile from "./pages/Profile.jsx";
import Sources from "./pages/Sources.jsx";
import AuditAssessment from "./pages/AuditAssessment.jsx";
import AuditAssessmentCopy from "./pages/AuditAssessmentCopy.jsx";
import AuditSnapshot from "./pages/AuditSnapshot.jsx";
import AdminTools from "./pages/AdminTools.jsx";
import SalesAffiliatePayoutAdmin from "./pages/SalesAffiliatePayoutAdmin.jsx";
import SalesAffiliateAdmin from "./pages/SalesAffiliateAdmin.jsx";
import Partners from "./pages/Partners.jsx";
import SalesAffiliateApplication from "./pages/SalesAffiliateApplication.jsx";
import SalesAffiliateOnboarding from "./pages/SalesAffiliateOnboarding.jsx";
import SalesAffiliateAgreement from "./pages/SalesAffiliateAgreement.jsx";
import SalesAffiliateDashboard from "./pages/SalesAffiliateDashboard.jsx";
import SalesAffiliateProspects from "./pages/SalesAffiliateProspects.jsx";
import SalesAffiliateSubscribers from "./pages/SalesAffiliateSubscribers.jsx";
import SalesAffiliateEarnings from "./pages/SalesAffiliateEarnings.jsx";
import SalesAffiliatePaymentSetupGuide from "./pages/SalesAffiliatePaymentSetupGuide.jsx";
import SalesAffiliateLearningCenter from "./pages/SalesAffiliateLearningCenter.jsx";
import SalesAffiliateSubscriberExperience from "./pages/SalesAffiliateSubscriberExperience.jsx";
import { SalesAffiliateWorkspaceProvider } from "./context/SalesAffiliateWorkspaceContext.jsx";
import {
  PlatformSecurityCapabilityProvider,
  usePlatformSecurityCapabilities,
} from "./context/PlatformSecurityCapabilityContext.jsx";

const FREE_ROUTES = new Set([
  "/",
  "/partners",
  "/universal-audit",
  "/universal-audit/results",
  "/pricing",
  "/subscription",
  "/terms",
  "/legal",
  "/privacy",
  "/auth",
  "/auth/callback",
  "/reset-password",
  "/payment-success",
]);

function normalizePathname(pathname) {
  const raw = String(pathname || "").trim();
  if (!raw || raw === "/") return "/";
  return raw.replace(/\/+$/g, "");
}

function buildNext({ pathname, search, hash }) {
  return encodeURIComponent(`${pathname}${search || ""}${hash || ""}`);
}

function isReportsRoute(pathname) {
  const path = normalizePathname(pathname);
  return path === "/reports" || path.startsWith("/audit-snapshot/");
}

function isRemediationRoute(pathname) {
  return normalizePathname(pathname) === "/remediation";
}

function isAdminToolsRoute(pathname) {
  return normalizePathname(pathname) === "/admin-tools";
}

function isSalesAffiliatePayoutAdminRoute(pathname) {
  return normalizePathname(pathname) === "/admin-tools/sales-affiliate-payouts";
}

function isSalesAffiliateAdminRoute(pathname) {
  return normalizePathname(pathname) === "/admin-tools/sales-affiliates";
}

function isRouteFree(pathname) {
  return FREE_ROUTES.has(normalizePathname(pathname));
}

function isPricingRoute(pathname) {
  return normalizePathname(pathname) === "/pricing";
}

function isSalesAffiliateRoute(pathname) {
  const path = normalizePathname(pathname);
  return path === "/sales-affiliate/application" || path.startsWith("/sales-affiliate/");
}

/* ---------- DOMAIN GUARD (SAFE INSERT) ---------- */
function DomainGuard({ children }) {
  const hostname = window.location.hostname;
  const pathname = normalizePathname(window.location.pathname);
  const search = window.location.search || "";
  const hash = window.location.hash || "";

  const isApp = hostname.startsWith("app.");
  const isRoot = hostname === "aifineprotector.com" || hostname === "www.aifineprotector.com";

  const rootAllowedPaths = new Set(["/", "/partners"]);
  const rootToAppPaths = new Set(
    Array.from(FREE_ROUTES).filter((path) => !rootAllowedPaths.has(path))
  );

  if (isRoot) {
    if (rootAllowedPaths.has(pathname)) {
      return children;
    }

    if (rootToAppPaths.has(pathname) || isSalesAffiliateRoute(pathname)) {
      window.location.href = `https://app.aifineprotector.com${pathname}${search}${hash}`;
      return null;
    }

    window.location.href = "/";
    return null;
  }

  if (isApp && pathname === "/") {
    window.location.href = "/dashboard";
    return null;
  }

  return children;
}
/* ------------------------------------------------ */

function AccessSpinner() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 text-slate-900">
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold shadow-sm">
        Checking access…
      </div>
    </div>
  );
}

function AccessVerificationError({ onRetry }) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 px-4 text-slate-900">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
        <div className="text-lg font-black">We couldn't verify your access</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          AFP could not confirm your subscription permissions right now. Your account has not been changed.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-xl bg-slate-950 px-5 py-3 text-sm font-extrabold text-white hover:bg-slate-800"
        >
          Retry Access Check
        </button>
      </div>
    </div>
  );
}

function PaywallGuard({ children }) {
  const location = useLocation();
  const pathname = normalizePathname(location.pathname);
  const search = location.search || "";
  const hash = location.hash || "";

  const {
    loading: entitlementLoading,
    isAuthenticated,
    isPaid,
    allowReports,
    allowRemediation,
    error: entitlementError,
    refresh: refreshEntitlement,
  } = useEntitlement();

  const [sessionReady, setSessionReady] = useState(false);

  const {
    status: capabilityStatus,
    hasCapability,
  } = usePlatformSecurityCapabilities();

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await supabase.auth.getSession();
        if (!mounted) return;
      } catch {
        if (!mounted) return;
      } finally {
        if (mounted) setSessionReady(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const pricingRoute = isPricingRoute(pathname);

  if (pricingRoute) {
    if (!sessionReady || entitlementLoading) return <AccessSpinner />;
    return children;
  }

  if (isRouteFree(pathname)) return children;

  if (!sessionReady || entitlementLoading) return <AccessSpinner />;

  if (entitlementError && !isAuthenticated) {
    return <AccessVerificationError onRetry={refreshEntitlement} />;
  }

  if (!isAuthenticated) {
    const next = buildNext({ pathname, search, hash });
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  if (isSalesAffiliateRoute(pathname)) {
    return children;
  }

  if (isSalesAffiliateAdminRoute(pathname)) {
    if (capabilityStatus === "idle" || capabilityStatus === "loading") {
      return <AccessSpinner />;
    }

    if (
      capabilityStatus !== "ready" ||
      !hasCapability("MANAGE_AFFILIATE_POLICY_OVERRIDES")
    ) {
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  }

  if (isSalesAffiliatePayoutAdminRoute(pathname)) {
    if (capabilityStatus === "idle" || capabilityStatus === "loading") {
      return <AccessSpinner />;
    }

    if (capabilityStatus !== "ready" || !hasCapability("MANAGE_PAYOUTS")) {
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  }

  if (isAdminToolsRoute(pathname)) {
    if (capabilityStatus === "idle" || capabilityStatus === "loading") {
      return <AccessSpinner />;
    }

    if (
      capabilityStatus !== "ready" ||
      !hasCapability("MANAGE_PLATFORM_SECURITY")
    ) {
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  }

  if (entitlementError) {
    return <AccessVerificationError onRetry={refreshEntitlement} />;
  }

  if (!isPaid) return <Navigate to="/pricing" replace />;

  if (isReportsRoute(pathname) && !allowReports) {
    return <Navigate to="/pricing" replace />;
  }

  if (isRemediationRoute(pathname) && !allowRemediation) {
    return <Navigate to="/pricing" replace />;
  }

  return children;
}

export default function App() {
  return (
    <Router>
      <PlatformSecurityCapabilityProvider>
        <DomainGuard>
          <PaywallGuard>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/partners" element={<Partners />} />
            <Route
              path="/sales-affiliate/application"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliateApplication />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route
              path="/sales-affiliate/onboarding"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliateOnboarding />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route
              path="/sales-affiliate/agreement"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliateAgreement />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route
              path="/sales-affiliate/dashboard"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliateDashboard />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route
              path="/sales-affiliate/prospects"
              element={<SalesAffiliateWorkspaceProvider><SalesAffiliateProspects /></SalesAffiliateWorkspaceProvider>}
            />
            <Route
              path="/sales-affiliate/subscribers"
              element={<SalesAffiliateWorkspaceProvider><SalesAffiliateSubscribers /></SalesAffiliateWorkspaceProvider>}
            />
            <Route
              path="/sales-affiliate/earnings"
              element={<SalesAffiliateWorkspaceProvider><SalesAffiliateEarnings /></SalesAffiliateWorkspaceProvider>}
            />
            <Route
              path="/sales-affiliate/payment-setup-guide"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliatePaymentSetupGuide />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route
              path="/sales-affiliate/learning-center"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliateLearningCenter />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route
              path="/sales-affiliate/learning-center/subscriber-experience"
              element={
                <SalesAffiliateWorkspaceProvider>
                  <SalesAffiliateSubscriberExperience />
                </SalesAffiliateWorkspaceProvider>
              }
            />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/start-audit" element={<PaidAssessmentEntryGate><StartAudit /></PaidAssessmentEntryGate>} />
            <Route path="/start-audit-legacy" element={<PaidAssessmentEntryGate><StartAuditLegacy /></PaidAssessmentEntryGate>} />
            <Route path="/start-audit/profile" element={<PaidAssessmentEntryGate><StartAuditProfile /></PaidAssessmentEntryGate>} />
            <Route path="/universal-audit" element={<UniversalRiskAudit />} />
            <Route path="/universal-audit/results" element={<UniversalRiskAuditResults />} />
            <Route path="/assessment" element={<PaidAssessmentEntryGate><AuditAssessment /></PaidAssessmentEntryGate>} />
            <Route path="/assessment-copy" element={<PaidAssessmentEntryGate><AuditAssessmentCopy /></PaidAssessmentEntryGate>} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/audit-snapshot/:snapshotId" element={<AuditSnapshot />} />
            <Route path="/remediation" element={<RemediationHub />} />
            <Route path="/ai-news" element={<AINews />} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/history" element={<History />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/sources" element={<Sources />} />
            <Route path="/admin-tools" element={<AdminTools />} />
            <Route path="/admin-tools/sales-affiliates" element={<SalesAffiliateAdmin />} />
            <Route path="/admin-tools/sales-affiliate-payouts" element={<SalesAffiliatePayoutAdmin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PaywallGuard>
        </DomainGuard>
      </PlatformSecurityCapabilityProvider>
    </Router>
  );
}
